import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

import LABELS from './labels.json';

/**
 * 옛 안전보건관리시스템(bomisafe.iptime.org · 캠게이트) 보관함 — 백엔드 LegacyArchiveController(V592 · DOC-136). 읽기만.
 * 칸 이름표는 옛 상세 화면에서 뽑은 것(labels.json · safe/dump/tools/step6-labels.py)에 공통 이름을 덧댄다.
 */

export type LegacySite = { siteName: string; siteId: string | null; count: number; firstDate: string | null; lastDate: string | null };
export type LegacyRow = { source: string; code: string; recordDate: string | null; siteName: string | null; siteId: string | null; listRow: Record<string, unknown> };
export type LegacyPage = { total: number; page: number; size: number; rows: LegacyRow[] };
export type LegacyFile = { name: string; url: string | null; bytes: number | null; missing: boolean };
export type LegacyDetail = LegacyRow & { detail: Record<string, unknown> | null; files: Record<string, LegacyFile> };

/** 옛 메뉴 이름 — 옛 사이트 메뉴 글 그대로. */
export const SOURCE_LABELS: Record<string, string> = {
  dyTbm_List: 'TBM',
  dySafe_List: '안전보건일지',
  sfEdu_List: '교육일지',
  jbWork_Main_List: '작업계획서',
  ckGong_Main_List: '공종별 위험성평가',
  ckWork_Main_List: '위험성평가 회의',
  ckWork_List: '공종별 위험성평가 관리',
  jbRisk_List: '위험공종(작업허가)',
  dyWeek_List: '주간안전관리현황',
  nsCons_List: '합동안전점검',
  nsConf_List: '노사협의체',
  sfAdd_List: '부적합사항 시정조치',
  dyMonth_List: '월간중점관리현황',
  sfInsp_List: '본사점검',
  nsSafe_List: '안전의날',
  waMove_List: '안전관계자 선임계',
  nsEmer_List: '비상사태대비훈련',
  sfOut_List: '외부기관점검',
  dyMonSafe_Main_List: '월간 안전보건관리 실적',
  dyDaily_List: '안전점검순회일지',
  sjAcc_List: '산업재해조사표',
};

/** 상세 묶음 이름(옛 요청 이름) → 화면 제목. 본문 행은 목록 줄과 같아 따로 보이지 않는다. */
export const PART_LABELS: Array<[RegExp, string]> = [
  [/_Mid_List|_Mid_Distinct/, '세부 항목'],
  [/_Sub_List/, '세부 내용'],
  [/MainImg_List/, '사진'],
  [/MainAttack_List/, '첨부'],
  [/SignMain_Process_List/, '결재'],
  [/SignMain_List/, '결재선'],
  [/sjAcc_List/, '조사 내용'],
];

/** 여러 메뉴가 같이 쓰는 칸의 이름. 옛 화면에서 못 뽑은 칸만 이것으로 채운다. */
const COMMON: Record<string, string> = {
  rdate: '일자', wdate: '작업일', fdate: '시작일', tdate: '종료일', regdate: '작성일시', wname: '현장', cpname: '업체',
  gmname: '공종', gname: '공종', sfname: '작성자', weather: '날씨', title: '제목', posit: '장소', target: '대상',
  teacher: '강사', name: '구분', detail: '내용', wdesc: '작업내용', mcnt: '인원', ecnt: '인원', ehour1: '시작',
  ehour2: '종료', edesc1: '교육내용', edesc2: '비고', edesc3: '특이사항', desc1: '내용', desc2: '내용 2', desc3: '내용 3',
  desc4: '내용 4', lname: '평가자', cdesc: '반려사유', jdesc1: '작업', jdesc2: '위험요인', jdesc3: '감소대책',
  jname1: '담당', jname2: '확인', bname: '재해형태', glev: '위험성', glev2: '개선 후 위험성', fname: '파일',
};

/** 화면에 보이지 않는 내부 칸 — 번호 · 코드 · 지운 표시 · 결재 상태 숫자. 파일 이름은 사진 · 첨부 칸에서 보인다. */
const HIDDEN = new Set([
  'code', 'sfcode', 'wacode', 'cpcode', 'isdel', 'ddate', 'del_sfcode', 'mno', 'kind', 'gmn', 'gdn', 'sign_prs', 'scnt1',
  'scnt2', 'dkind', 'mgn', 'man', 'smn', 'no', 'pkind', 'wkind', 'isact', 'ret', 'sn', 'sn2', 'bcode', 'cksn', 'ssn',
  'viewimg1', 'viewimg2', 'img1', 'img2', 'fsize', 'fsize1', 'fsize2', 'viewfname', 'signimg', 'attack', 'ekind', 'ckind',
]);

const LABEL_MAP = LABELS as Record<string, Record<string, string>>;

export function labelOf(source: string, key: string): string | null {
  if (HIDDEN.has(key)) return null;
  return LABEL_MAP[source]?.[key] ?? COMMON[key] ?? null;
}

/** 목록 한 줄의 요약 — 제목 · 작업내용 · 첫 내용 칸 중 처음 것. */
export function summaryOf(row: Record<string, unknown>): string {
  for (const key of ['title', 'wdesc', 'desc1', 'edesc1', 'target', 'name', 'jdesc1']) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.replace(/\s+/g, ' ').trim();
  }
  return '';
}

const BASE = '/v1/safe/legacy';
const qs = (params: Record<string, string | number | null | undefined>) =>
  new URLSearchParams(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => [k, String(v)])).toString();

export function useLegacySites(enabled = true) {
  return useQuery({ queryKey: ['legacy', 'sites'], queryFn: () => apiFetch<LegacySite[]>(`${BASE}/sites`), enabled });
}

export type LegacyScope = { site?: string | null; siteId?: string | null };

export function useLegacySources(scope: LegacyScope) {
  return useQuery({
    queryKey: ['legacy', 'sources', scope.site ?? null, scope.siteId ?? null],
    queryFn: () => apiFetch<Array<{ source: string; count: number }>>(`${BASE}/sources?${qs({ site: scope.site, siteId: scope.siteId })}`),
  });
}

export function useLegacyRecords(scope: LegacyScope, source: string, from: string, to: string, page: number) {
  return useQuery({
    queryKey: ['legacy', 'records', scope.site ?? null, scope.siteId ?? null, source, from, to, page],
    queryFn: () => apiFetch<LegacyPage>(`${BASE}/records?${qs({ site: scope.site, siteId: scope.siteId, source, from, to, page })}`),
  });
}

/** 옛 현장 ↔ 우리 현장 짝짓기(안전 권한). siteId null 이면 뗀다. */
export function useLegacyLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { siteName: string; siteId: string | null }) => apiFetch<number>(`${BASE}/sites/link`, { method: 'PUT', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['legacy'] }),
  });
}

export function useLegacyRecord(source: string | null, code: string | null) {
  return useQuery({
    queryKey: ['legacy', 'one', source, code],
    queryFn: () => apiFetch<LegacyDetail>(`${BASE}/records/${encodeURIComponent(source!)}/${encodeURIComponent(code!)}`),
    enabled: !!source && !!code,
  });
}
